import React, { useState, useMemo } from 'react';
import './ForgotPassword.css';
import { authService } from '../../services/authService';

// Password validation function (matches SignupForm)
function evaluatePassword(pw) {
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumber = /[0-9]/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  const not3repeat = !/(.)\1\1/.test(pw);
  const categories = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  const atLeast3 = categories >= 3;
  const lengthOk = pw.length >= 8 && pw.length <= 128;
  const passesMin = atLeast3 && not3repeat && lengthOk;
  return { hasLower, hasUpper, hasNumber, hasSpecial, not3repeat, atLeast3, lengthOk, passesMin };
}

// Password requirements component (matches SignupForm)
function PasswordRequirements({ policy }) {
  const requirements = [
    { key: 'lengthOk', text: 'At least 8 characters', met: policy.lengthOk },
    { key: 'atLeast3', text: 'At least 3 of: lowercase, uppercase, number, or special character', met: policy.atLeast3 },
    { key: 'not3repeat', text: 'No more than 2 identical characters in a row', met: policy.not3repeat },
  ];

  return (
    <div className="fp-pw-requirements" aria-live="polite">
      {requirements.map(req => (
        <div key={req.key} className={`fp-pw-req ${req.met ? 'met' : 'unmet'}`}>
          <span className="fp-pw-req-icon">{req.met ? '✓' : '○'}</span>
          <span className="fp-pw-req-text">{req.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function ForgotPassword({ onClose, defaultEmail = '' }) {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'password' | 'success'
  const [email, setEmail] = useState(defaultEmail || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  const policy = useMemo(() => evaluatePassword(newPassword), [newPassword]);
  const emailValid = /.+@.+\..+/.test(email.trim());
  const codeValid = /^\d{6}$/.test(code);
  const passwordValid = policy.passesMin;
  const passwordsMatch = newPassword === confirmPassword;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid || loading) return;

    setLoading(true);
    setError(null);
    try {
      await authService.forgotPassword(email.trim());
      setStep('code');
    } catch (err) {
      if (err.status === 429) {
        setError('Too many reset requests. Please try again later.');
      } else {
        setError('Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!codeValid || loading) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authService.verifyResetCode(email.trim(), code);
      setResetToken(response.reset_token);
      setStep('password');
    } catch (err) {
      if (err.status === 400) {
        setError('Invalid or expired code. Please try again.');
      } else {
        setError('Failed to verify code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid || !passwordsMatch || loading) return;

    setLoading(true);
    setError(null);
    try {
      await authService.resetPassword(email.trim(), newPassword, resetToken);
      setStep('success');
    } catch (err) {
      if (err.status === 400) {
        setError('Invalid or expired reset session. Please start over.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fp-overlay" role="dialog" aria-modal="true" aria-labelledby="fp-title">
      <div className="fp-modal">
        {/* Step 1: Email entry */}
        {step === 'email' && (
          <>
            <h3 id="fp-title">Forgot your password?</h3>
            <form onSubmit={handleEmailSubmit}>
              <p>Enter your email address and we'll send you a verification code.</p>
              <input
                className="fp-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@mail.com"
                autoFocus
              />
              {error && <div className="fp-error" role="alert">{error}</div>}
              <div className="fp-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!emailValid || loading}>
                  {loading ? 'Sending…' : 'Send code'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2: Code verification */}
        {step === 'code' && (
          <>
            <h3 id="fp-title">Enter verification code</h3>
            <form onSubmit={handleCodeSubmit}>
              <p>We sent a 6-digit code to <strong>{email}</strong>. Enter it below.</p>
              <input
                className="fp-input fp-code-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
              />
              {error && <div className="fp-error" role="alert">{error}</div>}
              <div className="fp-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep('email')}>
                  Back
                </button>
                <button type="submit" className="btn-primary" disabled={!codeValid || loading}>
                  {loading ? 'Verifying…' : 'Verify code'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 3: New password */}
        {step === 'password' && (
          <>
            <h3 id="fp-title">Set new password</h3>
            <form onSubmit={handlePasswordSubmit}>
              <p>Choose a new password for your account.</p>
              <div className="fp-password-field">
                <input
                  className="fp-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  autoFocus
                />
                <button
                  type="button"
                  className="fp-eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                      <path d="M2 2l20 20" stroke="currentColor" strokeWidth="1.75"/>
                      <path d="M3 12s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6z" fill="none" stroke="currentColor" strokeWidth="1.75"/>
                      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.75"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                      <path d="M3 12s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6z" fill="none" stroke="currentColor" strokeWidth="1.75"/>
                      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.75"/>
                    </svg>
                  )}
                </button>
              </div>
              {newPassword && <PasswordRequirements policy={policy} />}
              <input
                className="fp-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              {confirmPassword && !passwordsMatch && (
                <div className="fp-hint">Passwords do not match</div>
              )}
              {error && <div className="fp-error" role="alert">{error}</div>}
              <div className="fp-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep('code')}>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!passwordValid || !passwordsMatch || loading}
                >
                  {loading ? 'Resetting…' : 'Reset password'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <>
            <h3 id="fp-title">Password reset successful</h3>
            <div className="fp-success" role="status">
              Your password has been reset successfully. You can now log in with your new password.
            </div>
            <div className="fp-actions">
              <button type="button" className="btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
